import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    TextField,
    Typography,
} from "@material-ui/core"
import { useEffect, useState } from "react"
import { RegistrarLogErro } from "../../../services/clientefiel"
import { cancelarNotaFiscal, EmitirNotaFiscal } from "../../../services/integracao"
import {
    BaixarNotaFiscalPDF,
    emitirNotaFiscalConsumidor,
    sincronizarSituacaoNotaFiscal,
} from "../../../services/notasFiscais"

const ETAPA_VERIFICANDO = "verificando"
const ETAPA_SEM_NOTA = "sem-nota"
const ETAPA_EMITINDO = "emitindo"
const ETAPA_COM_NOTA = "com-nota"
const ETAPA_CANCELANDO = "cancelando"
const ETAPA_ERRO = "erro"

const formatarCpfCnpj = valor => {
    const digitos = (valor || "").replace(/\D/g, "").slice(0, 14)
    if (digitos.length <= 11) {
        return digitos
            .replace(/(\d{3})(\d)/, "$1.$2")
            .replace(/(\d{3})(\d)/, "$1.$2")
            .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
    }
    return digitos
        .replace(/^(\d{2})(\d)/, "$1.$2")
        .replace(/^(\d{2}\.\d{3})(\d)/, "$1.$2")
        .replace(/\.(\d{3})(\d)/, ".$1/$2")
        .replace(/(\d{4})(\d{1,2})$/, "$1-$2")
}

export default function DialogEmissaoFiscal({ open, onClose, pedido, alertStart }) {
    const [etapa, setEtapa] = useState(ETAPA_VERIFICANDO)
    const [erroMsg, setErroMsg] = useState("")
    const [erroEmissao, setErroEmissao] = useState("")
    const [cpfCnpj, setCpfCnpj] = useState("")
    const [justificativa, setJustificativa] = useState("")
    const [confirmandoCancelar, setConfirmandoCancelar] = useState(false)
    const [confirmandoSaida, setConfirmandoSaida] = useState(false)

    const verificarSituacao = async () => {
        setEtapa(ETAPA_VERIFICANDO)
        setErroMsg("")
        try {
            const resp = await sincronizarSituacaoNotaFiscal(pedido.id)
            if (resp?.retornoErro) {
                setErroMsg(resp.mensagem || "Erro ao verificar situação da nota")
                setEtapa(ETAPA_ERRO)
                return
            }
            setEtapa(resp.existe ? ETAPA_COM_NOTA : ETAPA_SEM_NOTA)
        } catch (error) {
            RegistrarLogErro(error, "DialogEmissaoFiscal.verificarSituacao", {
                idPedido: pedido?.id,
            })
            setErroMsg(error?.message || "Erro inesperado")
            setEtapa(ETAPA_ERRO)
        }
    }

    useEffect(() => {
        if (open && pedido?.id) {
            setCpfCnpj("")
            setJustificativa("")
            setConfirmandoCancelar(false)
            setConfirmandoSaida(false)
            setErroEmissao("")
            verificarSituacao()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, pedido?.id])

    const handleEmitir = async () => {
        setErroEmissao("")
        setEtapa(ETAPA_EMITINDO)
        const documento = cpfCnpj.replace(/\D/g, "")
        const resp = await emitirNotaFiscalConsumidor(pedido.id, documento)
        if (resp?.retornoErro) {
            const motivo = resp.mensagem || "Falha ao emitir a nota fiscal"
            setErroEmissao(motivo)
            alertStart?.(motivo, "error")
            setEtapa(ETAPA_SEM_NOTA)
            return
        }
        alertStart?.("Nota fiscal emitida com sucesso", "success")
        setEtapa(ETAPA_COM_NOTA)
    }

    const handleBaixar = async () => {
        try {
            const response = await BaixarNotaFiscalPDF(pedido.id)
            try {
                const responseJSON = JSON.parse(await response.text())
                if (responseJSON.codErro) {
                    alertStart?.(responseJSON.mensagem, "warning")
                    return
                }
            } catch {
                // PDF normal — segue o fluxo
            }
            const blob = new Blob([response], { type: "application/pdf" })
            const objectURL = URL.createObjectURL(blob)
            const link = document.createElement("a")
            link.href = objectURL
            link.download = pedido.id + ".pdf"
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(objectURL)
        } catch (error) {
            alertStart?.(error.message, "error")
            RegistrarLogErro(error, "DialogEmissaoFiscal.handleBaixar", { idPedido: pedido.id })
        }
    }

    const handleImprimir = async () => {
        const retorno = await EmitirNotaFiscal(pedido.id)
        if (retorno?.retornoErro) {
            alertStart?.(retorno.mensagem, "error")
            RegistrarLogErro(retorno, "DialogEmissaoFiscal.handleImprimir", { idPedido: pedido.id })
        }
    }

    const handleConfirmarCancelamento = async () => {
        if (justificativa.trim().length < 3) {
            alertStart?.("Informe uma justificativa com pelo menos 3 caracteres", "warning")
            return
        }
        setEtapa(ETAPA_CANCELANDO)
        const response = await cancelarNotaFiscal({
            id: pedido.id,
            justificativaCancelamentoNotaFiscal: justificativa,
        })
        alertStart?.(
            response?.mensagem ?? response?.message ?? "Solicitação enviada",
            response?.codErro ? "error" : "info"
        )
        setConfirmandoCancelar(false)
        setJustificativa("")
        await verificarSituacao()
    }

    const renderConteudo = () => {
        if (etapa === ETAPA_VERIFICANDO) {
            return (
                <Box display="flex" flexDirection="column" alignItems="center" py={3}>
                    <CircularProgress />
                    <Typography variant="body2" style={{ marginTop: 12 }}>
                        Verificando situação da nota fiscal…
                    </Typography>
                </Box>
            )
        }

        if (etapa === ETAPA_EMITINDO) {
            return (
                <Box display="flex" flexDirection="column" py={3}>
                    <Typography variant="body2" align="center" gutterBottom>
                        Emitindo nota fiscal, aguarde…
                    </Typography>
                    <LinearProgress />
                </Box>
            )
        }

        if (etapa === ETAPA_CANCELANDO) {
            return (
                <Box display="flex" flexDirection="column" py={3}>
                    <Typography variant="body2" align="center" gutterBottom>
                        Cancelando nota fiscal, aguarde…
                    </Typography>
                    <LinearProgress />
                </Box>
            )
        }

        if (etapa === ETAPA_ERRO) {
            return (
                <Box py={2}>
                    <Typography color="error" variant="body2">
                        {erroMsg}
                    </Typography>
                </Box>
            )
        }

        if (etapa === ETAPA_SEM_NOTA) {
            return (
                <Box display="flex" flexDirection="column" py={1} style={{ gap: 12 }}>
                    <Typography variant="body2">
                        Este pedido ainda não possui nota fiscal emitida.
                    </Typography>
                    <TextField
                        label="CPF ou CNPJ na nota (opcional)"
                        variant="outlined"
                        value={cpfCnpj}
                        onChange={e => setCpfCnpj(formatarCpfCnpj(e.target.value))}
                        fullWidth
                        size="small"
                        placeholder="000.000.000-00"
                    />
                    {erroEmissao ? (
                        <Box
                            style={{
                                backgroundColor: "#fdecea",
                                border: "1px solid #f5c2c0",
                                color: "#611a15",
                                padding: "8px 12px",
                                borderRadius: 4,
                            }}
                        >
                            <Typography
                                variant="caption"
                                style={{ fontWeight: 600, display: "block" }}
                            >
                                Falha ao emitir a nota:
                            </Typography>
                            <Typography variant="body2">{erroEmissao}</Typography>
                        </Box>
                    ) : null}
                </Box>
            )
        }

        if (etapa === ETAPA_COM_NOTA) {
            if (confirmandoCancelar) {
                return (
                    <Box display="flex" flexDirection="column" py={1} style={{ gap: 12 }}>
                        <Typography variant="body2">
                            Informe a justificativa para o cancelamento da nota.
                        </Typography>
                        <TextField
                            label="Justificativa"
                            variant="outlined"
                            value={justificativa}
                            onChange={e => setJustificativa(e.target.value)}
                            fullWidth
                            multiline
                            minRows={3}
                        />
                    </Box>
                )
            }
            return (
                <Box py={1}>
                    <Typography variant="body2">
                        Nota fiscal já emitida. Escolha uma ação:
                    </Typography>
                </Box>
            )
        }

        return null
    }

    const renderAcoes = () => {
        if (etapa === ETAPA_VERIFICANDO) {
            return (
                <Button onClick={onClose} disabled>
                    Aguarde
                </Button>
            )
        }

        if (etapa === ETAPA_EMITINDO || etapa === ETAPA_CANCELANDO) {
            return <Button onClick={solicitarFechamento}>Fechar</Button>
        }

        if (etapa === ETAPA_ERRO) {
            return (
                <>
                    <Button onClick={onClose}>Fechar</Button>
                    <Button variant="contained" color="primary" onClick={verificarSituacao}>
                        Tentar Novamente
                    </Button>
                </>
            )
        }

        if (etapa === ETAPA_SEM_NOTA) {
            return (
                <>
                    <Button onClick={onClose}>Fechar</Button>
                    <Button variant="contained" color="primary" onClick={handleEmitir}>
                        Emitir Nota Fiscal do Consumidor Eletrônica
                    </Button>
                </>
            )
        }

        if (etapa === ETAPA_COM_NOTA) {
            if (confirmandoCancelar) {
                return (
                    <>
                        <Button
                            onClick={() => {
                                setConfirmandoCancelar(false)
                                setJustificativa("")
                            }}
                        >
                            Voltar
                        </Button>
                        <Button
                            variant="contained"
                            style={{ backgroundColor: "#dc3545", color: "white" }}
                            onClick={handleConfirmarCancelamento}
                        >
                            Confirmar Cancelamento
                        </Button>
                    </>
                )
            }
            return (
                <Box display="flex" width="100%" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
                    <Button onClick={onClose}>Fechar</Button>
                    <Box style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <Button
                            variant="contained"
                            style={{ backgroundColor: "#dc3545", color: "white" }}
                            onClick={() => setConfirmandoCancelar(true)}
                        >
                            Cancelar Nota
                        </Button>
                        <Button variant="contained" onClick={handleImprimir}>
                            Imprimir
                        </Button>
                        <Button variant="contained" color="primary" onClick={handleBaixar}>
                            Baixar PDF
                        </Button>
                    </Box>
                </Box>
            )
        }

        return null
    }

    const bloqueado = etapa === ETAPA_EMITINDO || etapa === ETAPA_CANCELANDO

    const solicitarFechamento = () => {
        if (bloqueado) {
            setConfirmandoSaida(true)
            return
        }
        onClose?.()
    }

    const confirmarSaida = () => {
        setConfirmandoSaida(false)
        onClose?.()
    }

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason === "backdropClick") return
                solicitarFechamento()
            }}
            onClick={e => e.stopPropagation()}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle>Emissão Fiscal</DialogTitle>
            <DialogContent dividers>{renderConteudo()}</DialogContent>
            <DialogActions>{renderAcoes()}</DialogActions>
            <Dialog
                open={confirmandoSaida}
                onClose={() => setConfirmandoSaida(false)}
                onClick={e => e.stopPropagation()}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Sair da emissão?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        A geração da nota fiscal está em andamento, você pode voltar aqui quando
                        quiser pra confirmar o status. Deseja realmente sair?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmandoSaida(false)}>Não</Button>
                    <Button variant="contained" color="primary" onClick={confirmarSaida}>
                        Sim
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    )
}
